import { z } from "zod";

/**
 * Reference-data wire schemas — Truck, Customer, Property, DumpsterSize,
 * Landfill, Tenant. Shapes match the Unit #1 Drizzle columns (camelCase) with
 * `tenantId` exposed on the wire for multi-tenant scoping.
 */

export const Truck = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  unitNumber: z.number().int(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  year: z.number().int().nullable(),
  isActive: z.boolean(),
});
export type Truck = z.infer<typeof Truck>;

export const Customer = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  companyName: z.string(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  // SPEC-51 import prep: billing/contact email (nullable; the customer SELECT
  // column lists must return `email` or this parse fails loudly — intended).
  email: z.string().nullable(),
  billingAddressLine1: z.string(),
  billingAddressLine2: z.string().nullable(),
  billingCity: z.string(),
  billingState: z.string(),
  billingZip: z.string(),
  isActive: z.boolean(),
});
export type Customer = z.infer<typeof Customer>;

export const Property = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  customerId: z.string().uuid(),
  siteName: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  lat: z.string().nullable(),
  lng: z.string().nullable(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  // SPEC-51 import prep: free-text service note per site (nullable).
  serviceNotes: z.string().nullable(),
  isActive: z.boolean(),
});
export type Property = z.infer<typeof Property>;

export const DumpsterSize = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  yards: z.number().int(),
  label: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});
export type DumpsterSize = z.infer<typeof DumpsterSize>;

export const Landfill = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  address: z.string().nullable(),
  isActive: z.boolean(),
});
export type Landfill = z.infer<typeof Landfill>;

export const Tenant = z.object({
  id: z.string().uuid(),
  name: z.string(),
  phone: z.string().nullable(),
  // DISPATCH-PHONE (PM 2026-09-17): fallback #2 for the driver "Call
  // Dispatch" button — used only when no active-session dispatcher has a
  // phone. Set in Tenant Configuration, under Office Phone.
  dispatcherDefaultPhone: z.string().nullable(),
  photoRetentionMonths: z.number().int().nullable(),
  // Unit #18 additions: erReturnMinutes + defaultDoBillableCents
  // (er_return_minutes + default_do_billable_cents columns on the row).
  erReturnMinutes: z.number().int(),
  defaultDoBillableCents: z.number().int(),
  // Unit #21.5 addition: timeclockNfcRequired
  timeclockNfcRequired: z.boolean(),
  // SPEC-43 addition: officeKeyRequired
  officeKeyRequired: z.boolean(),
  // SPEC-68 §3.1: TRUE = "a user with a passkey must present it for a FULL
  // session" (per-user, credential-gated, never blocks login). Stranded
  // comments: see §3.5 for the fallback session semantics.
  passkeysRequired: z.boolean(),
  // SPEC-67 §3.8: NULL = binding enforced; non-NULL = binding DISABLED until
  // this instant (derived boolean below is what the UI should render).
  deviceBindingDisabledUntil: z.string().datetime().nullable(),
  // SPEC-67 §3.8: derived IN SQL (`SELECT (device_binding_disabled_until IS
  // NULL OR now() > device_binding_disabled_until) AS enforced`) — never
  // computed client-side from a client clock.
  deviceBindingEnforced: z.boolean(),
});
export type Tenant = z.infer<typeof Tenant>;

// ── Input schemas (POST/PATCH bodies) ──────────────────────────────────────
// Response shapes above require `id`/`tenantId`/`isActive`, so they cannot
// validate request bodies. These input schemas use `.optional()` for omitted
// fields and `.nullable()` only for optional string columns that a client may
// explicitly clear to NULL (per the "nullable-optional" convention).

export const NewTruck = z.object({
  unitNumber: z.number().int().min(1).max(9999),
  make: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  year: z.number().int().min(1980).max(2100).optional(),
});
export type NewTruck = z.infer<typeof NewTruck>;

export const TruckUpdate = NewTruck.partial().extend({
  isActive: z.boolean().optional(),
});
export type TruckUpdate = z.infer<typeof TruckUpdate>;

export const NewDumpsterSize = z.object({
  yards: z.number().int().min(1).max(100),
  label: z.string().min(1).max(200),
  sortOrder: z.number().int().min(0),
});
export type NewDumpsterSize = z.infer<typeof NewDumpsterSize>;

export const UpdateDumpsterSize = NewDumpsterSize.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateDumpsterSize = z.infer<typeof UpdateDumpsterSize>;

export const NewLandfill = z.object({
  name: z.string().min(1).max(200),
  address: z.string().nullable().optional(),
});
export type NewLandfill = z.infer<typeof NewLandfill>;

export const UpdateLandfill = NewLandfill.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateLandfill = z.infer<typeof UpdateLandfill>;

export const NewCustomer = z.object({
  companyName: z.string().min(1).max(200),
  billingAddressLine1: z.string().min(1),
  billingCity: z.string().min(1),
  billingState: z.string().min(1),
  billingZip: z.string().min(1),
  contactName: z.string().nullable().optional(),
  contactPhone: z.string().nullable().optional(),
  billingAddressLine2: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
});
export type NewCustomer = z.infer<typeof NewCustomer>;

export const CustomerUpdate = NewCustomer.partial().extend({
  isActive: z.boolean().optional(),
});
export type CustomerUpdate = z.infer<typeof CustomerUpdate>;

export const NewProperty = z.object({
  customerId: z.string().uuid(), // validated against the URL param by the route
  siteName: z.string().min(1),
  addressLine1: z.string().min(1),
  city: z.string().min(1),
  state: z.string().min(1),
  zip: z.string().min(1),
  addressLine2: z.string().nullable().optional(),
  lat: z.string().nullable().optional(),
  lng: z.string().nullable().optional(),
  contactName: z.string().nullable().optional(),
  contactPhone: z.string().nullable().optional(),
  serviceNotes: z.string().max(2000).nullable().optional(),
});
export type NewProperty = z.infer<typeof NewProperty>;

export const PropertyUpdate = NewProperty.partial().omit({
  customerId: true,
  lat: true,
  lng: true,
}).extend({
  isActive: z.boolean().optional(),
});
export type PropertyUpdate = z.infer<typeof PropertyUpdate>;



